import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { depositAndMint } from "../api/actions";
import { getEscrowRate, type EscrowRate } from "../api/nftdropsApi";
import { getTokenBalance, hasContractDeployed } from "../api/atomicApi";
import { logActivity } from "../api/activityLog";
import { useWallet } from "../wallet/WalletContext";
import { chain, contracts } from "../config";
import { showSuccess, showError } from "../components/Dialog";

export function Mint() {
  const { session, account, connect, connecting } = useWallet();
  const [templateId, setTemplateId] = useState("");
  const [rate, setRate] = useState<EscrowRate | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState<string | null>(null);
  // null while checking — avoids flashing the warning for a moment before
  // we know either way.
  const [escrowDeployed, setEscrowDeployed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    hasContractDeployed(chain.url, contracts.escrow).then((deployed) => {
      if (!cancelled) setEscrowDeployed(deployed);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!templateId) {
      setRate(null);
      setRateError(null);
      return;
    }
    let cancelled = false;
    setLookingUp(true);
    setRateError(null);
    getEscrowRate(templateId)
      .then((r) => {
        if (cancelled) return;
        if (!r || !r.enabled) {
          setRate(null);
          setRateError("No active escrow rate for this template — it isn't mintable as a backed NFT (yet, or ever).");
        } else {
          setRate(r);
        }
      })
      .catch(() => {
        if (!cancelled) setRateError("Couldn't reach escrowbacker — is it deployed yet?");
      })
      .finally(() => {
        if (!cancelled) setLookingUp(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  useEffect(() => {
    if (!account || !rate) {
      setBalance(null);
      return;
    }
    const symbol = rate.backing.quantity.split(" ")[1];
    getTokenBalance(chain.url, rate.backing.contract, account, symbol)
      .then(setBalance)
      .catch(() => setBalance(null));
  }, [account, rate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!session) {
      await connect();
      return;
    }
    if (!rate) return;
    const [amount, symbol] = rate.backing.quantity.split(" ");
    setBusy(true);
    try {
      await depositAndMint(session, { amount, symbol: symbol as "WAX" | "EOS", templateId });
      logActivity({
        type: "mint_backed_deposit",
        account: session.actor.toString(),
        templateId,
        amount: `${amount} ${symbol}`,
      });
      showSuccess("Your backed NFT will appear in My Assets in a few seconds.", "Deposit sent");
    } catch (err) {
      showError(err, "Mint failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow">
      <div className="page__header">
        <h1>Mint a Backed NFT</h1>
        <p className="page__subtitle">
          Pick a template that's registered with the escrow contract and deposit exactly its fixed backing amount
          to mint a claim-NFT redeemable for that amount later. Prefer a scheduled release with a time window,
          whitelist, or per-wallet limit? Use <Link to="/drops">Drops</Link> instead — it supports backed mints too.
        </p>
      </div>

      {escrowDeployed === false && (
        <div className="callout callout--warning">
          ⚠️ escrowbacker (<code>{contracts.escrow}</code>) hasn't been deployed yet, so transactions from this page
          will fail until it is live.
        </div>
      )}

      <form className="mint-form" onSubmit={handleSubmit}>
        <label>
          Template ID
          <input
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="e.g. 123456"
            required
          />
        </label>

        {lookingUp && <div className="mint-form__balance">Looking up this template's rate…</div>}
        {rateError && <div className="state-message state-message--error">{rateError}</div>}
        {rate && (
          <div className="callout">
            This template mints for exactly <strong>{rate.backing.quantity}</strong>, redeemable later by burning
            the NFT.
          </div>
        )}

        {account && rate && <div className="mint-form__balance">Wallet balance: {balance ?? "0.00000000"}</div>}

        <button className="btn btn--primary" type="submit" disabled={busy || !rate}>
          {!account ? (connecting ? "Connecting…" : "Connect wallet to mint") : busy ? "Depositing…" : "Deposit & Mint"}
        </button>
      </form>
    </div>
  );
}
